// Daemon CLIENT-IDENTITY keypair.
//
// Separate from `daemon-keypair.ts` (which holds the daemon's SERVER-role
// keypair — the one app clients talk to over the relay). This sibling file
// holds the keypair the daemon uses when it acts as a CLIENT of ANOTHER
// daemon — i.e. the cross-host SSH dialer.
//
// Why two keypairs instead of one:
//
//   - The server-role keypair is published in the daemon's pairing offer
//     (`/me/hosts/:id/offer`) and pinned by every client that pairs with this
//     host. Rotating it requires re-pairing every client.
//   - The client-identity keypair is enrolled as a `devices` row under the
//     host owner's user (via `POST /daemon/devices/pubkey`). Rotating it is
//     cheap: just re-enroll. Keeping it separate means a future need to rotate
//     the client identity (compromise, key-format upgrade) doesn't force a
//     re-pair storm.
//   - Same lifecycle and storage shape as `daemon-keypair.json` — persisted
//     once to `$APPOSTLE_HOME/daemon-client-identity.json` at 0o600, generated
//     lazily on first use.
//
// The PUBLIC half is uploaded to the auth-server (so daemon B's allow-list
// resolves daemon A's pubkey → owner userId → org membership → trust). The
// PRIVATE half NEVER leaves the daemon. Same hard invariant as the device key
// the app holds.

import { existsSync, readFileSync, writeFileSync } from "node:fs";
import path from "node:path";
import type pino from "pino";
import { z } from "zod";

import {
  exportPublicKey,
  exportSecretKey,
  generateKeyPair,
  importPublicKey,
  importSecretKey,
  type KeyPair,
} from "@appostle/relay/e2ee";

const KeyPairSchema = z.object({
  v: z.literal(2),
  publicKeyB64: z.string().min(1),
  secretKeyB64: z.string().min(1),
});

type StoredKeyPair = z.infer<typeof KeyPairSchema>;

const KEYPAIR_FILENAME = "daemon-client-identity.json";

export interface DaemonClientIdentity {
  keyPair: KeyPair;
  publicKeyB64: string;
}

/**
 * Load or create the daemon's client-identity keypair.
 *
 * Idempotent — safe to call multiple times across the lifetime of the daemon.
 * On a corrupt / unparseable file we log a warning and regenerate; that means
 * the old pubkey on the auth-server becomes inert (no devices row will resolve
 * to it) until the daemon re-enrolls. Re-enrollment happens automatically on
 * next boot via the bootstrap path that calls this function.
 */
export async function loadOrCreateDaemonClientIdentity(
  appostleHome: string,
  logger?: pino.Logger,
): Promise<DaemonClientIdentity> {
  const log = logger?.child({ module: "daemon-client-identity" });
  const filePath = path.join(appostleHome, KEYPAIR_FILENAME);

  if (existsSync(filePath)) {
    try {
      const raw = readFileSync(filePath, "utf8");
      const parsed = KeyPairSchema.parse(JSON.parse(raw)) as StoredKeyPair;

      const publicKey = importPublicKey(parsed.publicKeyB64);
      const secretKey = importSecretKey(parsed.secretKeyB64);
      const publicKeyB64 = exportPublicKey(publicKey);

      log?.info({ filePath }, "Loaded daemon client-identity keypair");
      return { keyPair: { publicKey, secretKey }, publicKeyB64 };
    } catch (error) {
      log?.warn(
        { err: error, filePath },
        "Failed to load daemon client-identity keypair, regenerating",
      );
    }
  }

  const keyPair = generateKeyPair();
  const publicKeyB64 = exportPublicKey(keyPair.publicKey);
  const secretKeyB64 = exportSecretKey(keyPair.secretKey);

  const payload: StoredKeyPair = {
    v: 2,
    publicKeyB64,
    secretKeyB64,
  };

  writeFileSync(filePath, JSON.stringify(payload, null, 2) + "\n", { mode: 0o600 });
  log?.info({ filePath }, "Generated daemon client-identity keypair");

  return { keyPair, publicKeyB64 };
}
